import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Medal, Trophy, Gamepad, Brain } from 'lucide-react';
import useUsersListPage from '../../../hooks/useUsersListPage';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs';
import {
  GameInstance,
  GameState,
  NimGameState,
  CodeQuizGameState,
  SafeDatabaseUser,
} from '../../../types/types';
import { getGames } from '../../../services/gamesService';
import BreadcrumbNav, { BreadcrumbItemProps } from '../../common/BreadcrumbNav';

// Interface for player stats in Nim game
interface NimPlayerStats {
  username: string;
  wins: number;
}

// Interface for player stats in Quiz game
interface QuizPlayerStats {
  username: string;
  totalScore: number;
  gamesPlayed: number;
  averageScore: number;
}

const LeaderboardPage = () => {
  const { allUsers } = useUsersListPage();
  const navigate = useNavigate();
  const [nimLeaderboard, setNimLeaderboard] = useState<NimPlayerStats[]>([]);
  const [quizLeaderboard, setQuizLeaderboard] = useState<QuizPlayerStats[]>([]);
  const [loading, setLoading] = useState({
    nim: true,
    quiz: true,
  });

  // Process Nim games to create leaderboard data
  const processNimGames = (games: GameInstance<GameState>[]) => {
    const playerWinsMap: Record<string, number> = {};

    games.forEach(game => {
      const nimState = game.state as NimGameState;
      if (nimState.winners && nimState.winners.length > 0) {
        nimState.winners.forEach(winner => {
          playerWinsMap[winner] = (playerWinsMap[winner] || 0) + 1;
        });
      }
    });

    // Convert map to array and sort by wins
    const leaderboard = Object.entries(playerWinsMap)
      .map(([username, wins]) => ({ username, wins }))
      .sort((a, b) => b.wins - a.wins)
      .slice(0, 3); // Top 3 players

    setNimLeaderboard(leaderboard);
    setLoading(prev => ({ ...prev, nim: false }));
  };

  // Process Quiz games to create leaderboard data
  const processQuizGames = (games: GameInstance<GameState>[]) => {
    const playerScoresMap: Record<string, { totalScore: number; gamesPlayed: number }> = {};

    games.forEach(game => {
      const quizState = game.state as CodeQuizGameState;

      if (quizState.playerScores) {
        Object.entries(quizState.playerScores).forEach(([playerId, scoreData]) => {
          if (!playerScoresMap[playerId]) {
            playerScoresMap[playerId] = { totalScore: 0, gamesPlayed: 0 };
          }

          playerScoresMap[playerId].totalScore += scoreData.score;
          playerScoresMap[playerId].gamesPlayed += 1;
        });
      }
    });

    // Convert map to array and sort by total score
    const leaderboard = Object.entries(playerScoresMap)
      .map(([username, stats]) => ({
        username,
        totalScore: stats.totalScore,
        gamesPlayed: stats.gamesPlayed,
        averageScore: stats.gamesPlayed > 0 ? Math.round(stats.totalScore / stats.gamesPlayed) : 0,
      }))
      .sort((a, b) => b.totalScore - a.totalScore)
      .slice(0, 3); // Top 3 players

    setQuizLeaderboard(leaderboard);
    setLoading(prev => ({ ...prev, quiz: false }));
  };

  // Fetch game data for leaderboards
  useEffect(() => {
    const fetchGameData = async () => {
      try {
        // Fetch completed Nim games
        const nimGames = await getGames('Nim', 'OVER');
        processNimGames(nimGames);

        // Fetch completed Quiz games
        const quizGames = await getGames('CodeQuiz', 'OVER');
        processQuizGames(quizGames);
      } catch (error) {
        // console.error('Error fetching game data:', error);
      }
    };

    fetchGameData();
  }, []);

  // Sort users by rank (points) in descending order and take top 3
  const topUsers = [...allUsers]
    .sort((a, b) => {
      const pointsA = (a as SafeDatabaseUser & { rank?: number }).rank || 0;
      const pointsB = (b as SafeDatabaseUser & { rank?: number }).rank || 0;
      return pointsB - pointsA;
    })
    .slice(0, 3);

  // Medal colors for top 3 positions
  const medalColors = ['text-yellow-500', 'text-gray-400', 'text-amber-700'];
  const medalNames = ['Gold', 'Silver', 'Bronze'];

  const handleUserClick = (username: string) => {
    navigate(`/user/${username}`);
  };

  // Define breadcrumb items
  const breadcrumbItems: BreadcrumbItemProps[] = [
    { label: 'Home', href: '/home' },
    { label: 'Leaderboard' },
  ];

  return (
    <div className='container py-8'>
      {/* Breadcrumb Navigation */}
      <BreadcrumbNav items={breadcrumbItems} />

      <div className='flex items-center mb-6'>
        <Trophy className='h-8 w-8 text-yellow-500 mr-3' />
        <h1 className='text-3xl font-bold'>Leaderboards</h1>
      </div>

      <Tabs defaultValue='users' className='w-full'>
        <TabsList className='grid w-full grid-cols-3 mb-6'>
          <TabsTrigger value='users' className='flex items-center gap-2'>
            <Medal className='h-4 w-4' />
            <span>Users</span>
          </TabsTrigger>
          <TabsTrigger value='nim' className='flex items-center gap-2'>
            <Gamepad className='h-4 w-4' />
            <span>Nim Game</span>
          </TabsTrigger>
          <TabsTrigger value='quiz' className='flex items-center gap-2'>
            <Brain className='h-4 w-4' />
            <span>Quiz Game</span>
          </TabsTrigger>
        </TabsList>

        {/* Users Leaderboard */}
        <TabsContent value='users'>
          <Card>
            <CardHeader className='pb-2'>
              <CardTitle className='text-2xl'>Top Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='space-y-4'>
                {topUsers.map((user, index) => {
                  // Extract the data we need with type assertion
                  const userWithExtras = user as SafeDatabaseUser & { rank?: number };
                  const pointsTotal = userWithExtras.rank || 0;
                  const rankLevel = Math.floor(pointsTotal / 15) + 1;

                  return (
                    <div
                      key={user.username}
                      className='group flex items-center justify-between border-b pb-2 last:border-0 cursor-pointer hover:bg-gray-50 p-4 rounded transition-all duration-300 ease-in-out hover:shadow-md hover:scale-[1.02] hover:border-gray-200'
                      onClick={() => handleUserClick(user.username)}>
                      <div className='flex items-center gap-4'>
                        <div className='relative'>
                          <Medal
                            className={`h-10 w-10 ${medalColors[index]} transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6`}
                            aria-label={`${medalNames[index]} Medal`}
                            strokeWidth={1.5}
                          />
                        </div>
                        <div className='flex flex-col'>
                          <span className='font-medium text-blue-500 group-hover:text-blue-600 transition-colors duration-300 text-lg'>
                            {user.username}
                          </span>
                          <span className='text-sm text-gray-500 group-hover:text-gray-600 transition-colors duration-300'>
                            Rank {index + 1}
                          </span>
                        </div>
                      </div>
                      <div className='text-right transition-transform duration-300 group-hover:translate-x-1'>
                        <div className='text-base group-hover:text-gray-800 transition-colors duration-300'>
                          Level: {rankLevel}
                        </div>
                        <div className='text-sm text-gray-500 group-hover:text-gray-600 transition-colors duration-300'>
                          {pointsTotal} points
                        </div>
                      </div>
                    </div>
                  );
                })}

                {topUsers.length === 0 && (
                  <div className='text-center py-4 text-gray-500'>No users to display</div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Nim Game Leaderboard */}
        <TabsContent value='nim'>
          <Card>
            <CardHeader className='pb-2'>
              <CardTitle className='text-2xl'>Top Nim Players</CardTitle>
            </CardHeader>
            <CardContent>
              {loading.nim ? (
                <div className='flex justify-center p-8'>
                  <div className='h-8 w-8 animate-spin rounded-full border-b-2 border-primary'></div>
                </div>
              ) : (
                <div className='space-y-4'>
                  {nimLeaderboard.map((player, index) => (
                    <div
                      key={player.username}
                      className='group flex items-center justify-between border-b pb-2 last:border-0 cursor-pointer hover:bg-gray-50 p-4 rounded transition-all duration-300 ease-in-out hover:shadow-md hover:scale-[1.02] hover:border-gray-200'
                      onClick={() => handleUserClick(player.username)}>
                      <div className='flex items-center gap-4'>
                        <div className='relative'>
                          <Medal
                            className={`h-10 w-10 ${medalColors[index]} transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6`}
                            aria-label={`${medalNames[index]} Medal`}
                            strokeWidth={1.5}
                          />
                        </div>
                        <div className='flex flex-col'>
                          <span className='font-medium text-blue-500 group-hover:text-blue-600 transition-colors duration-300 text-lg'>
                            {player.username}
                          </span>
                          <span className='text-sm text-gray-500 group-hover:text-gray-600 transition-colors duration-300'>
                            Rank {index + 1}
                          </span>
                        </div>
                      </div>
                      <div className='text-right transition-transform duration-300 group-hover:translate-x-1'>
                        <div className='text-base group-hover:text-gray-800 transition-colors duration-300'>
                          {player.wins} {player.wins === 1 ? 'win' : 'wins'}
                        </div>
                        <div className='text-sm text-gray-500 group-hover:text-gray-600 transition-colors duration-300'>
                          Nim Champion
                        </div>
                      </div>
                    </div>
                  ))}

                  {nimLeaderboard.length === 0 && (
                    <div className='text-center py-4 text-gray-500'>No Nim game data available</div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quiz Game Leaderboard */}
        <TabsContent value='quiz'>
          <Card>
            <CardHeader className='pb-2'>
              <CardTitle className='text-2xl'>Top Quiz Players</CardTitle>
            </CardHeader>
            <CardContent>
              {loading.quiz ? (
                <div className='flex justify-center p-8'>
                  <div className='h-8 w-8 animate-spin rounded-full border-b-2 border-primary'></div>
                </div>
              ) : (
                <div className='space-y-4'>
                  {quizLeaderboard.map((player, index) => (
                    <div
                      key={player.username}
                      className='group flex items-center justify-between border-b pb-2 last:border-0 cursor-pointer hover:bg-gray-50 p-4 rounded transition-all duration-300 ease-in-out hover:shadow-md hover:scale-[1.02] hover:border-gray-200'
                      onClick={() => handleUserClick(player.username)}>
                      <div className='flex items-center gap-4'>
                        <div className='relative'>
                          <Medal
                            className={`h-10 w-10 ${medalColors[index]} transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6`}
                            aria-label={`${medalNames[index]} Medal`}
                            strokeWidth={1.5}
                          />
                        </div>
                        <div className='flex flex-col'>
                          <span className='font-medium text-blue-500 group-hover:text-blue-600 transition-colors duration-300 text-lg'>
                            {player.username}
                          </span>
                          <span className='text-sm text-gray-500 group-hover:text-gray-600 transition-colors duration-300'>
                            Rank {index + 1}
                          </span>
                        </div>
                      </div>
                      <div className='text-right transition-transform duration-300 group-hover:translate-x-1'>
                        <div className='text-base group-hover:text-gray-800 transition-colors duration-300'>
                          {player.totalScore} points
                        </div>
                        <div className='text-sm text-gray-500 group-hover:text-gray-600 transition-colors duration-300'>
                          {player.gamesPlayed} {player.gamesPlayed === 1 ? 'game' : 'games'} played
                        </div>
                      </div>
                    </div>
                  ))}

                  {quizLeaderboard.length === 0 && (
                    <div className='text-center py-4 text-gray-500'>
                      No Quiz game data available
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LeaderboardPage;
